import { useEffect, useState } from 'react';
import { IonBadge, IonIcon, IonItem, IonLabel, IonList, IonNote, IonSpinner } from '@ionic/react';
import { carSportOutline } from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { api } from '../lib/api';
import { loadDriverSessions, removeDriverSession, type DriverSessionEntry, type Session } from '../lib/session';
import { STATUS_LABEL } from '../lib/status';
import type { Ride } from '../lib/types';

interface Row {
  session: DriverSessionEntry;
  ride: Ride;
}

interface Props {
  /** rideId der Fahrt, deren Standort gerade übertragen wird - oder keine. */
  trackedRideId: string | null;
  onOpen: (session: Session, ride: Ride) => void;
  onBack: () => void;
}

/**
 * Alle Fahrten, die dieses Gerät als Fahrer angelegt und noch nicht beendet
 * hat - unabhängig davon, ob gerade eine davon getrackt wird. Fahrten, die
 * der Server inzwischen selbst aufgeräumt hat (12 h TTL), fliegen beim
 * Nachschlagen still aus der Liste.
 */
export function ActiveRides({ trackedRideId, onOpen, onBack }: Props) {
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    const entries = loadDriverSessions();
    if (entries.length === 0) {
      setRows([]);
      return;
    }
    void Promise.all(
      entries.map(async (session): Promise<Row | null> => {
        try {
          const { ride } = await api.get(session.rideId, session.token);
          return { session, ride };
        } catch {
          removeDriverSession(session.rideId);
          return null;
        }
      }),
    ).then((results) => setRows(results.filter((row): row is Row => row !== null)));
  }, []);

  return (
    <Page title="Laufende Fahrten" onBack={onBack}>
      {rows === null ? (
        <div className="r2f-row" style={{ justifyContent: 'center', padding: 24 }}>
          <IonSpinner name="dots" />
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <h2>Keine laufenden Fahrten</h2>
          <p>Angelegte Fahrten stehen hier, solange sie nicht beendet oder verworfen sind.</p>
        </Card>
      ) : (
        <Card flush>
          <IonList lines="full">
            {rows.map(({ session, ride }) => (
              <IonItem key={session.rideId} button onClick={() => onOpen(session, ride)}>
                <IonIcon slot="start" icon={carSportOutline} color="primary" />
                <IonLabel>
                  <h3>{ride.code}</h3>
                  <IonNote>
                    {session.rideId === trackedRideId ? 'Wird gerade verfolgt' : STATUS_LABEL[ride.status]}
                  </IonNote>
                </IonLabel>
                {session.rideId === trackedRideId && <IonBadge color="success">Live</IonBadge>}
              </IonItem>
            ))}
          </IonList>
        </Card>
      )}
    </Page>
  );
}
